import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Sun, Moon, Globe, LogOut, LayoutDashboard, ChevronDown, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Logo = ({ className = "", showText = true, size = "md" }) => {
  // Mark PNG (new gold DAAM mark) + "DAAM" wordmark text. The mark is the
  // standalone glyph (D with embedded A triangle); the DAAM wordmark text
  // keeps the gold-gradient treatment to preserve the existing UI design.
  const markPx = size === "lg" ? 32 : size === "sm" ? 22 : 26;
  return (
    <Link
      to="/"
      data-testid="brand-logo"
      className={`inline-flex items-center gap-2 font-display font-semibold tracking-tight ${className}`}
    >
      <img
        src="/brand/daam-mark-gold-128.png?v=2"
        alt="DAAM"
        width={markPx}
        height={markPx}
        style={{ width: markPx, height: markPx, objectFit: "contain" }}
        data-testid="brand-logo-mark"
      />
      {showText && (
        <span
          className={`${size === "lg" ? "text-2xl" : size === "sm" ? "text-lg" : "text-xl"}`}
          style={{
            background: "linear-gradient(135deg,#F7E29F 0%,#EACB6E 25%,#C79A45 55%,#9E7326 85%,#6E4B16 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          DAAM
        </span>
      )}
    </Link>
  );
};

export const LangThemeToggle = () => {
  const { lang, toggleLang, theme, toggleTheme } = useI18n();
  return (
    <div className="flex items-center gap-1">
      <button data-testid="lang-toggle-btn" onClick={toggleLang} className="p-2 hover:bg-muted rounded-sm transition" title="Language">
        <Globe className="w-4 h-4" />
        <span className="sr-only">Language</span>
      </button>
      <span className="text-xs text-muted-foreground w-6 text-center">{lang.toUpperCase()}</span>
      <button data-testid="theme-toggle-btn" onClick={toggleTheme} className="p-2 hover:bg-muted rounded-sm transition" title="Theme">
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const NotificationsBell = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {}
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch {}
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell-btn" className="relative p-2 hover:bg-muted rounded-sm transition" aria-label="Notifications">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span data-testid="notif-unread-count" className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF4F00] text-white text-[10px] font-semibold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-auto">
        <div className="flex items-center justify-between px-2 py-1">
          <DropdownMenuLabel className="p-0">{t.notif.title}</DropdownMenuLabel>
          {unread > 0 && (
            <button data-testid="notif-mark-all-btn" onClick={markAll} className="text-xs text-primary hover:underline">
              {t.notif.markAll}
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {items.length === 0 && <div className="px-3 py-6 text-sm text-muted-foreground text-center">{t.notif.empty}</div>}
        {items.map((n) => (
          <div key={n.id} data-testid={`notif-item-${n.id}`} className={`px-3 py-2 border-b border-border/40 last:border-0 ${!n.read ? "bg-muted/40" : ""}`}>
            <div className="text-sm font-medium">{n.title}</div>
            <div className="text-xs text-muted-foreground">{n.body}</div>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const UserMenu = () => {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  if (!user) return null;

  const dashPath = user.role === "client" ? "/app/dashboard" : user.role === "team" ? "/app/dashboard" : "/app/admin";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-btn" className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-sm">
          <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <span className="text-sm hidden sm:inline">{user.name || user.email}</span>
          <ChevronDown className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="user-menu-dashboard" onClick={() => nav(dashPath)}>
          <LayoutDashboard className="w-4 h-4 me-2" /> {t.common.dashboard}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-profile" onClick={() => nav("/app/profile")}>
          <UserIcon className="w-4 h-4 me-2" /> {lang === "ar" ? "ملفّي" : "Profile"}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-logout" onClick={async () => { await logout(); nav("/"); }}>
          <LogOut className="w-4 h-4 me-2" /> {t.common.logout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const PublicHeader = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <header data-testid="public-header" className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/40">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-8 py-4">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="#features" className="hover:text-primary transition">{t.nav.features}</a>
          <a href="#pricing" className="hover:text-primary transition">{t.nav.pricing}</a>
          <a href="#contact" className="hover:text-primary transition">{t.nav.contact}</a>
        </nav>
        <div className="flex items-center gap-2">
          <LangThemeToggle />
          {user ? (
            <UserMenu />
          ) : (
            <>
              <Link to="/login" data-testid="header-signin-link" className="text-sm px-3 py-2 hover:bg-muted rounded-sm hidden sm:inline-block">{t.common.signIn}</Link>
              <Link to="/register" data-testid="header-signup-btn" className="nx-btn-primary text-sm py-2 px-4">{t.common.getStarted}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const AppHeader = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <header data-testid="app-header" className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/40">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
        <Logo />
        <div className="flex items-center gap-2">
          <LangThemeToggle />
          <NotificationsBell />
          <UserMenu />
        </div>
      </div>
    </header>
  );
};
